import { useState } from 'react';
import { Box, Typography, TextField, Button, DatePicker, TimePicker, ImageUpload,
    LocationAutocomplete, Loader, DeleteForeverOutlinedIcon, ConfirmationDialog } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { createStep, updateStep, deleteStep } from 'src/utils';
import { TRANSPORT_MODE_MAPPINGS } from 'src/data';

export default function AddEditStep({ 
  tripId, 
  tripStartDate,
  step, 
  onStepAdded, 
  onStepDeleted, 
  handleViewChange, 
  handleHttpSuccess, 
  handleHttpFailure,
  setDrawerState }) {

  const isEditMode = Boolean(step);
  const isMobile = useIsMobile();
  const formFontSize = isMobile ? 16 : 14;

  // Form state
  const [locationName, setLocationName] = useState(step?.point?.name || '');
  const [coords, setCoords] = useState(
    step?.point?.coordinates 
      ? { lng: step.point.coordinates[0], lat: step.point.coordinates[1] } 
      : null
  );
  const [stepName, setStepName] = useState(step?.name || '');
  const [date, setDate] = useState(step?.startDate ? new Date(step.startDate) : new Date());
  const [time, setTime] = useState(
    step?.startDate
      ? new Date(step.startDate)
      : (() => {
          const d = new Date();
          d.setHours(9, 0, 0, 0);
          return d;
        })()
  );
  const [description, setDescription] = useState(step?.description || '');
  const [previewUrl, setPreviewUrl] = useState(step?.imageUrls[0] || null);
  const [transportMethod, setTransportMethod] = useState(step?.transportMethod || 'Drive');
  const [countryIsoAlpha2, setCountryIsoAlpha2] = useState(step?.countryIsoAlpha2 || null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);

  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasLocation = !!locationName.trim();
  const isFormValid = hasLocation && stepName.trim().length > 0;

  const handleImageUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setSelectedImage(file);
    setImageChanged(true);

    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setImageChanged(true);
  };

  const handleCancelClick = () => {
    handleViewChange('TripDetails');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isFormValid) {
      setLoading(false);
      return;
    }

    const metadataPayload = {
      point: coords ? { coordinates: [coords.lng, coords.lat], name: locationName.trim() } : undefined,
      name: stepName.trim() || undefined,
      startDate: combineDateAndTime(date, time),
      description: description.trim() || undefined,
      transportMethod,
      countryIsoAlpha2,
      removeImage: imageChanged && !selectedImage
    };

    try {
      if (isEditMode) {
        // Use new updateStep which handles optional image
        const success = await updateStep(tripId, step.id, metadataPayload, selectedImage);
        if (success) {
          handleViewChange('TripDetails', null, {
            ...step,
            ...metadataPayload,
            imageUrls: selectedImage ? [previewUrl] : step.imageUrls
          });
          handleHttpSuccess('Step updated.');
        } else {
          handleHttpFailure('Step update failed.');
        }
      } else {
        // New step creation
        const payload = {
          ...metadataPayload,
          image: selectedImage
        };
        const result = await createStep(tripId, payload);
        onStepAdded();
        handleViewChange('TripDetails', null, result);
        handleHttpSuccess('Step added.');
        if (isMobile) setDrawerState('half');
      }
    } catch (err) {
      console.error('Error saving step:', err);
      handleHttpFailure('Error saving step.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
      setLoading(true);
      try {
          await deleteStep(tripId, step.id);
          onStepDeleted();
          handleViewChange('TripDetails');
          handleHttpSuccess('Step deleted.');
      } catch (err) {
          console.error('Error deleting trip:', err);
          handleHttpFailure('Error deleting step.');
      } finally {
          setLoading(false);
      }
  };

  return (
    <Box sx={{ m: 2, height: isMobile ? undefined : '100%' }}>
      {/* Top Bar */}
      {!isMobile && (
        <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 1 }}>

          <Button 
            disableRipple 
            onClick={handleCancelClick} 
            size="small">
              Cancel
          </Button>

          <Typography variant="h5" textAlign="center" flex={1}>
            {isEditMode ? 'Edit Step' : 'New Step'}
          </Typography>

          <Box width="64px" />

        </Box>
      )}

      <Box className="scrollBox" sx={{ height: isMobile ? '100%' : '90%', px: 2 }}>
        {/* Location Input */}
        <Typography variant="body2" fontWeight={600} sx={{ mt: isMobile ? 1 : 2, mb:1 }}>
          Location:
        </Typography>
        <LocationAutocomplete
            value={locationName}
            currentCoords={coords}
            onSelect={(loc) => {
                if (loc === null) {
                    setLocationName('');
                    setCoords(null);
                    setCountryIsoAlpha2(null);
                } else {
                    setLocationName(loc.label);
                    setCoords(loc.coords);
                    setCountryIsoAlpha2(loc.countryCode || null);
                }
            }}
            />

        {/* Disable rest if location not set */}
        <Box sx={{ opacity: hasLocation ? 1 : 0.3, pointerEvents: hasLocation ? 'auto' : 'none' }}>
          {/* Step Name */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>
            Step Name:
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. Day 1 – Exploring Old Town"
            value={stepName}
            onChange={(e) => setStepName(e.target.value.slice(0, 60))}
            helperText={`${stepName.length}/60`}
            inputProps={{ style: { fontSize: formFontSize } }}
            margin="dense"
            required
          />

          {/* Date / Time */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, width: '100%' }}>
            <Box sx={{width: '48%'}}>
              <Typography variant="body2" fontWeight={600}>Date:</Typography>
              <DatePicker sx={{mt: 1}} value={date} onChange={setDate} minDate={new Date(tripStartDate)} maxDate={new Date()} />
            </Box>
            <Box sx={{width: '48%'}}>
              <Typography variant="body2" fontWeight={600}>Time:</Typography>
              <TimePicker sx={{mt: 1}} value={time} onChange={setTime} />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            *Steps are ordered by date and time.
          </Typography>

          {/* Description */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 3 }}>
            Description:
          </Typography>
          <TextField
            fullWidth
            placeholder="What happened during this step?"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 250))}
            helperText={`${description.length}/250`}
            inputProps={{ style: { fontSize: formFontSize } }}
            multiline
            rows={3}
            margin="dense"
          />

          {/* <Typography variant="body2" fontWeight={600} sx={{ mt: 3 }}>
            Transport Mode:
          </Typography>
          <Select
            fullWidth
            value={transportMethod}
            onChange={(e) => setTransportMethod(e.target.value)}
            displayEmpty
            size="small"
            sx={{ mt: 1 }}
          >
            <MenuItem value="" disabled>Select a transport mode</MenuItem>
            {TRANSPORT_MODES.map((mode) => (
              <MenuItem key={mode.value} value={mode.value}>
                {mode.emoji} {mode.label}
              </MenuItem>
            ))}
          </Select> */}

          {/* Image Upload */}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 2 }}>
            Image:
          </Typography>
          {/*<ImageUpload
                previewUrl={previewUrl}
                onUpload={handleImageUpload}
                onClear={handleClearImage} />*/}
            <ImageUpload
              previewUrl={previewUrl}
              onUpload={handleImageUpload}
              onClear={handleClearImage}
              aspect={1}
              size={160}
            />
        </Box>

        {/* Submit & Delete */}
        <Box mt={3} mb={4} display="flex" justifyContent="space-around" alignItems="center" sx={{width: '100%'}}>
          {isEditMode && (

            <Button 
              variant="text" 
              color="error" 
              disabled={!isFormValid}
              onClick={() => setConfirmOpen(true)}>
                <DeleteForeverOutlinedIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>Delete Step</Typography>
            </Button>
          )}

          <Button
            fullWidth={!isEditMode}
            variant="contained"
            disabled={!isFormValid}
            onClick={handleSubmit}
          >
            {isEditMode ? 'Update Step' : 'Add Step'}
          </Button>
        </Box>
      </Box>

      {loading && <Loader />}

      <ConfirmationDialog
          open={confirmOpen}
          title="Delete Step"
          content="This action cannot be undone. Are you sure you want to delete this step?"
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
              setConfirmOpen(false);
              handleDelete();
          }}
          confirmText="Delete"
          />

    </Box>
  );
}


function combineDateAndTime(date, time) {
  if (!date || !time) return null;

  const combined = new Date(date); // clone date

  combined.setHours(time.getHours());
  combined.setMinutes(time.getMinutes());
  combined.setSeconds(time.getSeconds());
  combined.setMilliseconds(time.getMilliseconds());

  return combined;
}